/**
 * Whether the services are answering.
 *
 * The last thing asked of a service got an answer. A `4xx` is an answer (the
 * service is there and said no); no answer at all, a gateway's error page,
 * or a `5xx` is not. Fed by every query the app makes
 * (src/shared/api/query-client.ts); shown by the shell beside the state of
 * the event stream (src/shared/live), which is another matter: the services
 * may answer while the stream is down.
 */
import { useSyncExternalStore } from "react";

import { CLIENT_CODES, isProblem } from "./problem";

export interface Liveness {
  /** When a service last answered, as `Date.now()`; null: none has yet. */
  lastAnswerAt: number | null;
  /** The last request got no answer. */
  failing: boolean;
}

let state: Liveness = { lastAnswerAt: null, failing: false };
const listeners = new Set<() => void>();

function set(next: Liveness): void {
  state = next;
  for (const listener of listeners) listener();
}

/** True for a failure that means "no service answered", not "the service said no". */
export function isNoAnswer(error: unknown): boolean {
  if (!isProblem(error)) return false;
  return (
    error.code === CLIENT_CODES.network ||
    error.code === CLIENT_CODES.unexpected ||
    error.status >= 500
  );
}

export function reportSuccess(now: number = Date.now()): void {
  set({ lastAnswerAt: now, failing: false });
}

export function reportFailure(error: unknown, now: number = Date.now()): void {
  if (isNoAnswer(error)) set({ ...state, failing: true });
  // Refused is answered. Anything that is not a problem is a bug of the app's own.
  else if (isProblem(error)) set({ lastAnswerAt: now, failing: false });
}

/** For tests, and for signing out: nothing is known. */
export function resetLiveness(): void {
  set({ lastAnswerAt: null, failing: false });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useLiveness(): Liveness {
  return useSyncExternalStore(subscribe, () => state);
}
